import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { articlesData, featuredArticle } from '@/data/articles';
import { Button } from '@/components/ui/button';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ArrowRight, Search, Clock, ChevronRight, ChevronLeft } from 'lucide-react';

export default function ArticlePage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('Semua');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = ['Semua', 'Industri', 'Tips Branding', 'Maklon', 'Skincare', 'Parfum'];

  const articles = articlesData;

  const filteredArticles = activeCategory === 'Semua' 
    ? articles 
    : articles.filter(a => a.category.toLowerCase() === activeCategory.toLowerCase());

  return (
    <>
      <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
        <PublicNavbar />

        {/* Hero & Search Section */}
        <section className="pt-20 pb-16 px-6 lg:px-12 bg-gradient-to-b from-[#F9F5EC] to-[#FFFDF9] border-b border-[#E6D5B8]/30" data-aos="fade-up">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 text-[#302621]">
              Artikel & Insight
            </h1>
            <p className="text-base text-[#7A695B] mb-10 leading-relaxed font-light">
              Temukan tips, inspirasi, dan informasi terbaru seputar industri kosmetik, pengembangan produk, serta strategi membangun brand sukses.
            </p>

            <div className="relative max-w-xl mx-auto shadow-sm rounded-full overflow-hidden border border-[#E6D5B8]/50 bg-white flex items-center focus-within:border-[#D4AF37] transition-colors">
              <Search className="w-5 h-5 text-[#D4AF37] ml-6 absolute" />
              <input 
                type="text" 
                placeholder="Cari topik artikel..." 
                className="w-full py-4 pl-14 pr-6 bg-transparent outline-none text-[#302621] placeholder:text-gray-400 text-sm"
              />
              <Button className="absolute right-2 top-1.5 bottom-1.5 bg-[#D4AF37] hover:bg-[#B58B22] text-white rounded-full px-6 transition-colors border-0">
                Cari
              </Button>
            </div>
          </div>
        </section>

        {/* Featured Article */}
        <section className="py-16 px-6 lg:px-12 bg-white" data-aos="fade-up" data-aos-delay="100">
          <div className="container mx-auto max-w-7xl">
            <Link to={`/artikel/${featuredArticle.id}`} className="bg-[#FCFAF6] rounded-3xl overflow-hidden border border-[#E6D5B8]/40 flex flex-col lg:flex-row group cursor-pointer hover:shadow-lg transition-shadow duration-300">
              <div className="w-full lg:w-3/5 aspect-[16/9] lg:aspect-auto relative overflow-hidden">
                <img 
                  src={featuredArticle.img}
                  alt="Featured Article" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" 
                />
              </div>
              <div className="w-full lg:w-2/5 p-8 lg:p-12 flex flex-col justify-center">
                <span className="inline-block py-1 px-3 bg-[#E6D5B8]/40 text-[#8C6208] text-[10px] font-bold tracking-widest uppercase rounded mb-4 w-fit">
                  {featuredArticle.category}
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold text-[#302621] mb-4 leading-tight group-hover:text-[#D4AF37] transition-colors">
                  {featuredArticle.title}
                </h2>
                <p className="text-[#7A695B] text-sm leading-relaxed mb-8 flex-grow line-clamp-3">
                  {featuredArticle.content}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-xs text-[#7A695B] gap-3">
                    <span className="font-semibold text-[#302621]">{featuredArticle.author}</span>
                    <div className="w-1 h-1 rounded-full bg-[#E6D5B8]"></div>
                    <span>{featuredArticle.date}</span>
                    <div className="w-1 h-1 rounded-full bg-[#E6D5B8]"></div>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {featuredArticle.readTime}</span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>

        {/* Categories Filter */}
        <section className="px-6 lg:px-12 bg-white pb-8" data-aos="fade-up" data-aos-delay="200">
          <div className="container mx-auto max-w-7xl">
            <div className="flex overflow-x-auto pb-4 hide-scrollbar gap-2 lg:gap-4 border-b border-[#E6D5B8]/30">
              {categories.map((cat, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    activeCategory === cat 
                      ? 'bg-[#302621] text-[#E6D5B8]' 
                      : 'bg-[#FCFAF6] text-[#7A695B] hover:bg-[#E6D5B8]/30 border border-[#E6D5B8]/40'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-12 px-6 lg:px-12 bg-white min-h-[500px]" data-aos="fade-up" data-aos-delay="300">
          <div className="container mx-auto max-w-7xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {filteredArticles.map((article) => (
                <Link to={`/artikel/${article.id}`} key={article.id} className="group cursor-pointer flex flex-col">
                  <div className="w-full aspect-[16/10] bg-gray-100 rounded-2xl mb-6 overflow-hidden relative border border-[#E6D5B8]/20">
                    <img src={article.img} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => e.target.src = "https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=600&auto=format&fit=crop"} />
                  </div>
                  <span className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-widest mb-3">{article.category}</span>
                  <h4 className="font-bold text-[#302621] text-lg mb-4 leading-snug group-hover:text-[#D4AF37] transition-colors line-clamp-3">{article.title}</h4>
                  <div className="flex items-center text-xs text-[#7A695B] gap-3 mt-auto">
                    <span>{article.date}</span>
                    <div className="w-1 h-1 rounded-full bg-[#E6D5B8]"></div>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.readTime}</span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-2 mt-20">
              <button className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#D4AF37] transition-colors opacity-50 cursor-not-allowed">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button className="w-10 h-10 rounded-full flex items-center justify-center bg-[#302621] text-white font-bold shadow-sm">
                1
              </button>
              <button className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#D4AF37] transition-colors font-medium">
                2
              </button>
              <button className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#D4AF37] transition-colors font-medium">
                3
              </button>
              <span className="text-[#7A695B]">...</span>
              <button className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E6D5B8] text-[#7A695B] hover:bg-[#F9F5EC] hover:text-[#D4AF37] transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <PublicFooter />
      </div>
    </>
  );
}
